import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { dailyCheckinApi } from "../api/dailyCheckin";
import { wearablesApi } from "../api/wearables";
import { rciApi } from "../api/rci";
import { journalEntriesApi } from "../api/journalEntries";
import { dayLabel } from "../utils/dailyLabels";
import { daySignalLabel } from "../utils/dailySignalLabel";
import { DOMAIN_WORD, DOMAIN_META } from "../utils/domainLabels";
import { useBackOrFallback } from "../hooks/useBackOrFallback";
import { isOuraUIEnabled } from "../utils/features";

// /history/:date — single-day detail view for the Signal Timeline.
//
// Two API calls:
//   1. /checkin/history?date=  — snapshot + journal + protocols + today's wearable
//   2. /wearables/range?start=&end=  — 30-day window for HRV/sleep/RHR delta
//
// All sections are independently optional; if a section's data didn't
// exist that day the section renders with em-dashes or just doesn't
// render at all (journal, protocols).

const VELLUM = "#F2EDE3";
const ANTIQUE_GOLD = "#9E8456";
const WARM_BLACK = "#1C1814";
const TEXT_SECONDARY = "rgba(28,24,20,0.62)";
const BORDER = "rgba(28,24,20,0.10)";
const SERIF = "'Playfair Display', serif";
// "Bad" delta state — a rust tone, matching the existing "Missed"
// protocol badge on Dashboard.jsx (#9E5A4A) rather than inventing a
// new color. Previously a bad delta (e.g. a Resting HR spike) fell
// back to the same neutral TEXT_SECONDARY used for filler text, so it
// rendered indistinguishable from "nothing to report."
const RUST = "#9E5A4A";

// Snapshot window size, matching SignalHistory.jsx's PAGE_DAYS so the
// two screens' rolling-window statistics (mean/max/min) agree on the
// same day. Kept as a local constant rather than importing PAGE_DAYS
// from SignalHistory.jsx, which doesn't export it.
const SNAPSHOT_WINDOW_DAYS = 28;

const CARD_STYLE = {
  background: "white",
  borderRadius: 12,
  padding: "12px 14px",
  border: `0.5px solid ${BORDER}`,
};

const SECTION_HEADER_STYLE = {
  fontSize: 10,
  color: ANTIQUE_GOLD,
  fontWeight: 500,
  letterSpacing: "0.08em",
  margin: "0 0 6px",
};

const JOURNAL_PREVIEW_CHARS = 120;


function _parseLocalDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function _formatHeaderDate(iso) {
  const d = _parseLocalDate(iso);
  return {
    weekday: d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase(),
    monthDay: d.toLocaleDateString("en-US", { month: "long", day: "numeric" }),
  };
}

function _toScore(v) {
  return v == null ? null : Math.max(1, Math.round(v * 100));
}

// Compute the mean of a numeric field across an array of wearable
// rows EXCLUDING the target date — that's what makes the delta a
// "vs prior 29 days" comparison rather than a self-inclusive average.
function _meanExcept(rows, field, targetDate) {
  const vs = rows
    .filter(r => r.date !== targetDate && typeof r[field] === "number")
    .map(r => r[field]);
  if (!vs.length) return null;
  return vs.reduce((a, b) => a + b, 0) / vs.length;
}

function _fmtDelta(today, mean, unit, { lowerIsBetter = false } = {}) {
  if (today == null || mean == null) return null;
  const d = today - mean;
  if (Math.abs(d) < 0.01) return null;
  const positive = d > 0;
  // For HRV / Sleep, up is good (gold). For Resting HR, down is good
  // (gold). The label string itself always carries the sign.
  const isGood = lowerIsBetter ? !positive : positive;
  // Numeric formatting: HRV/RHR integer; sleep score 0.1 precision.
  let str;
  if (unit === "hr") {
    str = `${positive ? "+" : "−"}${Math.abs(d).toFixed(1)}${unit}`;
  } else {
    str = `${positive ? "+" : "−"}${Math.round(Math.abs(d))}${unit}`;
  }
  return { str, isGood };
}


export default function DayDetail() {
  const { date } = useParams();
  const navigate = useNavigate();
  // Deep-link safe back: when the user lands on /history/:date
  // directly (notification / shared link / pasted URL), there's no
  // SPA entry behind us — fall back to the timeline.
  const goBack = useBackOrFallback("/history");
  const { user, token } = useAuth();
  const [checkinHistory, setCheckinHistory] = useState(null);
  const [wearableRange, setWearableRange] = useState([]);
  const [recentSnapshots, setRecentSnapshots] = useState([]);
  const [journalExpanded, setJournalExpanded] = useState(false);
  // Count of free-write journal_entries on this date. Distinct from the
  // JOURNAL card below, which shows this day's CHECK-IN brain dump —
  // two different surfaces that both contain writing. Zero renders no
  // cross-link at all.
  const [journalEntryCount, setJournalEntryCount] = useState(0);
  // Tracked SEPARATELY from the count, because zero and "we don't know"
  // are different facts. Both used to render nothing, which made a dead
  // network indistinguishable from a quiet day — the one state where
  // silence is a lie. Zero still renders nothing (see the card below);
  // a failure says so in one line.
  const [journalFetchFailed, setJournalFetchFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !date) return;
    let cancelled = false;
    setLoading(true);

    // 30-day wearable window ending at the queried date.
    const target = _parseLocalDate(date);
    const start = new Date(target);
    start.setDate(start.getDate() - 29);
    const startIso = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;

    // Snapshot window for daySignalLabel/dayLabel — anchored on the
    // TARGET date, not "today" (the previous bug: a fixed 7-count
    // fetch with no ``before`` was always anchored at today, so it
    // silently excluded the viewed date whenever it was more than 7
    // snapshots back, leaving the headline blank). ``before`` is
    // strict less-than, so passing the day AFTER the target makes the
    // target date itself the first row returned. Window size matches
    // SignalHistory.jsx's PAGE_DAYS so both screens' window mean/max
    // agree on the same day.
    const dayAfterTarget = new Date(target);
    dayAfterTarget.setDate(dayAfterTarget.getDate() + 1);
    const beforeIso = `${dayAfterTarget.getFullYear()}-${String(dayAfterTarget.getMonth() + 1).padStart(2, "0")}-${String(dayAfterTarget.getDate()).padStart(2, "0")}`;

    Promise.all([
      dailyCheckinApi.history(token, date).catch(() => null),
      wearablesApi.range(startIso, date, token).catch(() => []),
      rciApi.history(user.id, SNAPSHOT_WINDOW_DAYS, token, beforeIso).catch(() => []),
      // "Did I free-write on this day?" — a single-date lookup via the
      // ?date= filter rather than pulling the whole journal down to
      // filter it here. Soft-fails, but the failure is CARRIED rather
      // than flattened into a zero: the outcome is wrapped so the
      // renderer can tell "none that day" from "couldn't ask".
      journalEntriesApi.listByDate(date, token)
        .then((res) => ({ ok: true, res }))
        .catch(() => ({ ok: false, res: null })),
    ]).then(([h, w, hist, j]) => {
      if (cancelled) return;
      setCheckinHistory(h);
      setWearableRange(Array.isArray(w) ? w : []);
      setRecentSnapshots(Array.isArray(hist) ? hist : []);
      setJournalFetchFailed(!j.ok);
      setJournalEntryCount(
        j.ok && Array.isArray(j.res?.entries) ? j.res.entries.length : 0
      );
    }).catch(() => {
      if (!cancelled) setError("Couldn't load this day.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user, token, date]);

  const header = useMemo(() => _formatHeaderDate(date), [date]);
  const signalScore = _toScore(checkinHistory?.rci_total);
  const bioScore = _toScore(checkinHistory?.bio_score);
  const psychScore = _toScore(checkinHistory?.psych_score);
  const behScore = _toScore(checkinHistory?.beh_score);

  // Per-day signal label + per-domain labels are derived from the
  // recent snapshot window (anchored on the target date, see the
  // fetch above). The target date is somewhere inside
  // ``recentSnapshots``; we locate its idx and pass that to the
  // helpers. Fallback to "" only when no snapshot exists for this
  // date at all (not merely "older than N days" anymore).
  const reverseChrono = recentSnapshots; // newest-first per API
  const targetIdx = reverseChrono.findIndex(s => s.window_end === date);
  // dayLabel / daySignalLabel expect chronological order (oldest →
  // newest) so they can look "backward" via i - 1.
  const chronoSnapshots = useMemo(
    () => [...reverseChrono].reverse(),
    [reverseChrono]
  );
  const chronoIdx = targetIdx >= 0
    ? chronoSnapshots.length - 1 - targetIdx
    : -1;

  const headline = chronoIdx >= 0
    ? daySignalLabel(chronoIdx, chronoSnapshots)
    : "";
  const bioLabel = chronoIdx >= 0
    ? dayLabel(chronoIdx, chronoSnapshots, "BIO")
    : "";
  const psychLabel = chronoIdx >= 0
    ? dayLabel(chronoIdx, chronoSnapshots, "PSYCH")
    : "";
  const behLabel = chronoIdx >= 0
    ? dayLabel(chronoIdx, chronoSnapshots, "BEH")
    : "";

  // Today's wearable readings, pulled from /checkin/history (the
  // single-day surface) — it's the same source the day-of card uses
  // and saves us from filtering wearableRange just for this row.
  const hrvToday = checkinHistory?.hrv ?? null;
  const sleepToday = checkinHistory?.sleep_score ?? null;
  const rhrToday = checkinHistory?.resting_hr ?? null;

  const hrvMean = useMemo(
    () => _meanExcept(wearableRange, "hrv", date),
    [wearableRange, date]
  );
  const sleepMean = useMemo(
    () => _meanExcept(wearableRange, "sleep_score", date),
    [wearableRange, date]
  );
  const rhrMean = useMemo(
    () => _meanExcept(wearableRange, "resting_hr", date),
    [wearableRange, date]
  );

  const hrvDelta = _fmtDelta(hrvToday, hrvMean, "ms");
  const sleepDelta = sleepToday != null && sleepMean != null
    ? _fmtDelta(sleepToday, sleepMean, "pts")
    : null;
  const rhrDelta = _fmtDelta(rhrToday, rhrMean, "bpm", { lowerIsBetter: true });

  const protocolsCompleted = checkinHistory?.protocols_completed || {};
  const journalText = checkinHistory?.journal_text || "";
  const hasJournal = journalText.length > 0;
  const journalPreview = journalText.length > JOURNAL_PREVIEW_CHARS
    ? journalText.slice(0, JOURNAL_PREVIEW_CHARS).trimEnd() + "…"
    : journalText;

  return (
    <div style={{
      minHeight: "100vh",
      background: VELLUM,
      padding: "max(20px, calc(env(safe-area-inset-top) + 12px)) 16px var(--safe-bottom)",
      boxSizing: "border-box",
    }}>
      {/* Back nav */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 20,
      }}>
        <button
          type="button"
          onClick={goBack}
          aria-label="Back to Signal history"
          style={{
            all: "unset", cursor: "pointer",
            fontSize: 18, color: ANTIQUE_GOLD, minWidth: 24,
          }}
        >
          ←
        </button>
        <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>
          Signal history
        </span>
      </div>

      {error && <p style={{ fontSize: 13, color: WARM_BLACK }}>{error}</p>}
      {loading && <p style={{ fontSize: 13, color: TEXT_SECONDARY }}>Loading…</p>}

      {!loading && !error && (
        <>
          {/* Date + Signal Score header */}
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "flex-start", marginBottom: 4,
          }}>
            <div>
              <p style={{
                fontSize: 11, color: ANTIQUE_GOLD, fontWeight: 500,
                letterSpacing: "0.08em", margin: "0 0 2px",
              }}>
                {header.weekday}
              </p>
              <p style={{
                fontSize: 22, fontFamily: SERIF, color: WARM_BLACK, margin: 0,
              }}>
                {header.monthDay}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{
                fontSize: 36, fontFamily: SERIF, color: ANTIQUE_GOLD,
                margin: 0, lineHeight: 1,
              }}>
                {signalScore ?? "—"}
              </p>
              <p style={{
                fontSize: 11, color: TEXT_SECONDARY, margin: 0,
              }}>
                signal score
              </p>
            </div>
          </div>

          {/* Italic headline */}
          {headline && (
            <p style={{
              fontSize: 13, color: WARM_BLACK,
              margin: "0 0 16px", fontStyle: "italic",
            }}>
              "{headline}."
            </p>
          )}

          {/* Domain grid */}
          {(bioScore != null || psychScore != null || behScore != null) && (
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8, marginBottom: 16,
            }}>
              {DOMAIN_META.map(({ code }) => ({
                code,
                score: { PSYCH: psychScore, BIO: bioScore, BEH: behScore }[code],
                label: { PSYCH: psychLabel, BIO: bioLabel, BEH: behLabel }[code],
              })).map(d => (
                <div key={d.code} style={{
                  background: "white", borderRadius: 10, padding: 10,
                  textAlign: "center",
                  border: `0.5px solid ${BORDER}`,
                }}>
                  <p style={{
                    fontSize: 10, color: ANTIQUE_GOLD, fontWeight: 500,
                    margin: "0 0 4px",
                  }}>
                    {DOMAIN_WORD[d.code] || d.code}
                  </p>
                  <p style={{
                    fontSize: 20, fontFamily: SERIF, color: WARM_BLACK, margin: 0,
                  }}>
                    {d.score ?? "—"}
                  </p>
                  <p style={{
                    fontSize: 10, color: TEXT_SECONDARY, margin: "4px 0 0",
                    lineHeight: 1.3,
                  }}>
                    {d.label || "—"}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Oura card */}
          {(hrvToday != null || sleepToday != null || rhrToday != null) && (
            <div style={{ ...CARD_STYLE, marginBottom: 10 }}>
              {/* Header reads WEARABLE in the native iOS build while
                  VITE_IOS_OURA_ENABLED is off (utils/features.js). */}
              <p style={SECTION_HEADER_STYLE}>{isOuraUIEnabled() ? "OURA" : "WEARABLE"}</p>
              <OuraRow label="HRV" value={hrvToday != null ? `${Math.round(hrvToday)}ms` : "—"} delta={hrvDelta} />
              <OuraRow label="Sleep" value={sleepToday != null ? `${Math.round(sleepToday)}` : "—"} delta={sleepDelta} />
              <OuraRow label="Resting HR" value={rhrToday != null ? `${Math.round(rhrToday)}bpm` : "—"} delta={rhrDelta} />
              <div style={{
                marginTop: 8,
                borderTop: `0.5px solid ${BORDER}`, paddingTop: 6,
              }}>
                <span style={{ fontSize: 10, color: TEXT_SECONDARY }}>
                  Compared with your own 30-day average
                </span>
              </div>
            </div>
          )}

          {/* Journal card */}
          {hasJournal && (
            <div style={{ ...CARD_STYLE, marginBottom: 10 }}>
              <p style={SECTION_HEADER_STYLE}>JOURNAL</p>
              <p style={{
                fontSize: 12, color: WARM_BLACK, margin: 0, lineHeight: 1.5,
              }}>
                "{journalExpanded ? journalText : journalPreview}"
              </p>
              {!journalExpanded && journalText.length > JOURNAL_PREVIEW_CHARS && (
                <button
                  type="button"
                  onClick={() => setJournalExpanded(true)}
                  style={{
                    all: "unset", cursor: "pointer",
                    fontSize: 11, color: ANTIQUE_GOLD,
                    margin: "6px 0 0", display: "inline-block",
                  }}
                >
                  Read full entry →
                </button>
              )}
            </div>
          )}

          {/* Free-write cross-link. Renders only when this date
              actually has journal_entries rows — an always-present
              "no entries" row would be noise on most days.

              Deliberately a link, not an inline excerpt: this screen is
              the signal view, and free-write text is excluded from
              signal. Showing the writing here would put it next to
              scores it had no part in producing. */}
          {journalEntryCount > 0 && (
            <button
              type="button"
              onClick={() => navigate(`/journal?date=${date}`)}
              style={{
                // ``all: unset`` first, then CARD_STYLE, so the card
                // chrome survives the button reset instead of being
                // wiped by it.
                all: "unset",
                ...CARD_STYLE,
                cursor: "pointer",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                marginBottom: 10,
              }}
              aria-label="Open journal entries from this day"
            >
              <span>
                <span style={{ ...SECTION_HEADER_STYLE, display: "block" }}>
                  FREE-WRITE
                </span>
                <span style={{ fontSize: 12, color: WARM_BLACK }}>
                  {journalEntryCount === 1
                    ? "1 entry from this day"
                    : `${journalEntryCount} entries from this day`}
                </span>
              </span>
              <span style={{ fontSize: 15, color: ANTIQUE_GOLD, flexShrink: 0 }}>›</span>
            </button>
          )}

          {/* The lookup failed. Deliberately NOT a FREE-WRITE card: it
              carries no count and no tap-through, because we don't know
              whether there is anything to tap through to. One line, so a
              dead network reads as a dead network and not as a day you
              didn't write on. */}
          {journalFetchFailed && (
            <p style={{
              fontSize: 11, color: TEXT_SECONDARY,
              margin: "0 0 10px", lineHeight: 1.5,
            }}>
              Couldn&rsquo;t check for journal entries.
            </p>
          )}

          {/* Protocols card */}
          <div style={CARD_STYLE}>
            <p style={SECTION_HEADER_STYLE}>PROTOCOLS</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <ProtocolRow done={!!protocolsCompleted.morning_anchor} label="Morning Anchor" />
              <ProtocolRow done={!!protocolsCompleted.midday_clarity_reset} label="Midday Clarity Reset" />
              <ProtocolRow done={!!protocolsCompleted.evening_winddown} label="Evening Wind-Down" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function OuraRow({ label, value, delta }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between",
      fontSize: 12, marginTop: 4,
    }}>
      <span style={{ color: TEXT_SECONDARY }}>{label}</span>
      <span style={{ color: WARM_BLACK, fontWeight: 500 }}>
        {value}{" "}
        {delta && (
          <span style={{
            color: delta.isGood ? ANTIQUE_GOLD : RUST,
            fontSize: 11,
          }}>
            {delta.str}
          </span>
        )}
      </span>
    </div>
  );
}


function ProtocolRow({ done, label }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, fontSize: 12,
    }}>
      <span style={{ color: done ? ANTIQUE_GOLD : TEXT_SECONDARY }}>
        {done ? "✓" : "—"}
      </span>
      <span style={{ color: done ? WARM_BLACK : TEXT_SECONDARY }}>
        {label}
      </span>
    </div>
  );
}
